<template>
  <div class="starred-page">
    <header class="hero">
      <div class="hero-copy">
        <p class="eyebrow">Saved for later</p>
        <h1>Starred</h1>
        <p class="lede">People and organizations you’ve pinned — one place to return.</p>
      </div>
      <div class="entity-tabs" role="tablist" aria-label="Starred type">
        <button
          type="button"
          role="tab"
          class="entity-tab"
          :class="{ active: entity === 'contacts' }"
          :aria-selected="entity === 'contacts'"
          @click="setEntity('contacts')"
        >
          Contacts
        </button>
        <button
          type="button"
          role="tab"
          class="entity-tab"
          :class="{ active: entity === 'orgs' }"
          :aria-selected="entity === 'orgs'"
          @click="setEntity('orgs')"
        >
          Organizations
        </button>
      </div>
    </header>

    <div v-if="entity === 'contacts'" class="scope-row">
      <div class="scope-tabs" role="tablist" aria-label="Contact star scope">
        <button
          type="button"
          class="scope-tab"
          :class="{ active: scope === 'assigned' }"
          @click="setScope('assigned')"
        >
          Assigned to me
        </button>
        <button
          type="button"
          class="scope-tab"
          :class="{ active: scope === 'mine' }"
          @click="setScope('mine')"
        >
          My stars
        </button>
        <button
          type="button"
          class="scope-tab"
          :class="{ active: scope === 'all' }"
          @click="setScope('all')"
        >
          All stars
        </button>
      </div>
    </div>

    <p v-if="isLoading" class="loading-message">Loading…</p>
    <template v-else>
      <p class="count-msg">{{ countLabel }}</p>

      <!-- Contacts table -->
      <div v-if="entity === 'contacts'" class="scroll-box">
        <table class="table starred-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Company</th>
              <th>Assigned</th>
              <th>Tags</th>
              <th v-if="scope === 'all'">Starred by</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in customers" :key="c.id">
              <td>
                <router-link :to="`/customers/${c.id}`" class="name-link">
                  {{ [c.first_name, c.last_name].filter(Boolean).join(' ') || '—' }}
                </router-link>
              </td>
              <td><span class="truncate" :title="c.main_email">{{ c.main_email || '—' }}</span></td>
              <td>
                <router-link
                  v-if="c.company"
                  class="org-link"
                  :to="{ path: '/companies/view', query: { name: c.company } }"
                >{{ c.company }}</router-link>
                <span v-else class="muted">—</span>
              </td>
              <td>
                <span v-if="c.chibi_contact" class="assigned-pill">{{ c.chibi_contact }}</span>
                <span v-else class="muted">—</span>
              </td>
              <td>
                <span v-for="t in (c.tags || [])" :key="t" class="tag-pill">{{ t }}</span>
                <span v-if="!(c.tags && c.tags.length)" class="muted">—</span>
              </td>
              <td v-if="scope === 'all'">
                <span v-if="(c.starred_by || []).length" class="starred-by">
                  {{ (c.starred_by || []).join(', ') }}
                </span>
                <span v-else class="muted" title="Starred before we tracked ownership">legacy</span>
              </td>
            </tr>
            <tr v-if="!customers.length">
              <td :colspan="scope === 'all' ? 6 : 5" class="empty-row">{{ emptyLabel }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Organizations -->
      <div v-else class="orgs-panel">
        <div v-if="!companies.length" class="empty-orgs">
          <p>{{ emptyLabel }}</p>
          <router-link class="empty-cta" to="/companies">Browse organizations</router-link>
        </div>
        <ul v-else class="org-grid" aria-label="Starred organizations">
          <li v-for="(co, idx) in companies" :key="co.company" class="org-card" :style="{ '--i': idx }">
            <div class="org-card-top">
              <button
                type="button"
                class="star-btn active"
                title="Unstar organization"
                :disabled="togglingCompany === co.company"
                @click="unstarCompany(co)"
              >★</button>
              <router-link
                class="org-name"
                :to="{ path: '/companies/view', query: { name: co.company } }"
              >{{ co.company }}</router-link>
            </div>
            <div class="org-meta">
              <span>{{ co.contacts_count || 0 }} contact{{ (co.contacts_count || 0) === 1 ? '' : 's' }}</span>
              <span v-if="co.category" class="dot">·</span>
              <span v-if="co.category">{{ co.category }}</span>
              <span v-if="co.lead_status" class="dot">·</span>
              <span v-if="co.lead_status">{{ co.lead_status }}</span>
            </div>
            <div v-if="(co.tags || []).length" class="org-tags">
              <span v-for="t in co.tags.slice(0, 6)" :key="t" class="tag-pill">{{ t }}</span>
            </div>
            <a
              v-if="co.website"
              class="website"
              :href="formatWebsite(co.website)"
              target="_blank"
              rel="noopener"
            >{{ co.website }}</a>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
import CustomersService from '../services/CustomersService'
import chibiTeamMixin from '@/mixins/chibiTeamMixin'

const VALID_SCOPES = ['all', 'mine', 'assigned']
const VALID_ENTITIES = ['contacts', 'orgs']

export default {
  name: 'StarredPage',
  mixins: [chibiTeamMixin],
  props: ['username'],
  data() {
    const savedScope = localStorage.getItem('starredScope')
    const savedEntity = localStorage.getItem('starredEntity')
    const queryEntity = this.$route.query.tab === 'orgs' || this.$route.query.entity === 'orgs'
      ? 'orgs'
      : (VALID_ENTITIES.includes(savedEntity) ? savedEntity : 'contacts')
    return {
      customers: [],
      companies: [],
      total: 0,
      isLoading: true,
      togglingCompany: null,
      scope: VALID_SCOPES.includes(savedScope) ? savedScope : 'assigned',
      entity: queryEntity
    }
  },
  computed: {
    countLabel() {
      const n = this.total
      const plural = n !== 1 ? 's' : ''
      if (this.entity === 'orgs') {
        return `${n} starred organization${plural}`
      }
      if (this.scope === 'assigned') {
        return `${n} contact${plural} assigned to you`
      }
      if (this.scope === 'mine') {
        return `${n} contact${plural} you starred`
      }
      return `${n} starred contact${plural} (anyone · includes older stars before we tracked who)`
    },
    emptyLabel() {
      if (this.entity === 'orgs') {
        return 'No starred organizations yet. Star one from Companies or an organization page.'
      }
      if (this.scope === 'assigned') {
        const names = this.chibiTeamNames || []
        const list = names.length
          ? (names.length === 1 ? names[0] : names.slice(0, -1).join(', ') + ', or ' + names[names.length - 1])
          : 'a Chibi teammate'
        return `No contacts assigned to you yet. Set “Chibi contact” on a profile to ${list}.`
      }
      if (this.scope === 'mine') {
        return 'You haven’t starred anyone yet. Star contacts from Contacts or a profile.'
      }
      return 'No starred contacts yet.'
    }
  },
  watch: {
    '$route.query.tab'(val) {
      if (val === 'orgs' && this.entity !== 'orgs') this.setEntity('orgs')
      if (val === 'contacts' && this.entity !== 'contacts') this.setEntity('contacts')
    }
  },
  created() {
    this.load()
  },
  methods: {
    setEntity(entity) {
      if (this.entity === entity) return
      this.entity = entity
      localStorage.setItem('starredEntity', entity)
      const q = { ...this.$route.query, tab: entity === 'orgs' ? 'orgs' : 'contacts' }
      this.$router.replace({ query: q }).catch(() => {})
      this.load()
    },
    setScope(scope) {
      if (this.scope === scope) return
      this.scope = scope
      localStorage.setItem('starredScope', scope)
      this.load()
    },
    formatWebsite(url) {
      const s = String(url || '').trim()
      if (!s) return '#'
      return /^https?:\/\//i.test(s) ? s : `https://${s}`
    },
    async load() {
      this.isLoading = true
      try {
        if (this.entity === 'orgs') {
          await this.loadCompanies()
        } else {
          await this.loadContacts()
        }
      } finally {
        this.isLoading = false
      }
    },
    async loadContacts() {
      try {
        const params = { per_page: 500, page: 1 }
        if (this.scope === 'assigned') {
          params.assigned = 'me'
        } else if (this.scope === 'mine') {
          params.starred = 'mine'
        } else {
          params.starred = 'all'
        }
        const { data } = await CustomersService.list(params)
        this.total = data.total
        this.customers = data.customers || []
        this.companies = []
      } catch (err) {
        console.error('Load starred contacts failed', err)
        this.customers = []
        this.total = 0
      }
    },
    async loadCompanies() {
      try {
        const { data } = await CustomersService.listCompanies({
          starred: 1,
          per_page: 200,
          page: 1
        })
        this.companies = data.companies || []
        this.total = data.total ?? this.companies.length
        this.customers = []
      } catch (err) {
        console.error('Load starred orgs failed', err)
        this.companies = []
        this.total = 0
      }
    },
    async unstarCompany(co) {
      if (!co?.company) return
      this.togglingCompany = co.company
      try {
        const { data } = await CustomersService.toggleCompanyStar(co.company)
        if (!data.starred) {
          this.companies = this.companies.filter((c) => c.company !== co.company)
          this.total = this.companies.length
        }
      } catch (err) {
        console.error('Unstar company failed', err)
        alert('Could not update star.')
      } finally {
        this.togglingCompany = null
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');

.starred-page {
  --sea: #1abc9c;
  --ink: #eef7f4;
  --muted: rgba(238, 247, 244, 0.62);
  padding: 0.65rem 0.5rem 2rem;
  color: var(--ink);
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
}

.hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding-bottom: 0.9rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.eyebrow {
  margin: 0 0 0.15rem;
  font-size: 0.7rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--sea);
  font-weight: 600;
}
.hero h1 {
  margin: 0;
  font-family: Fraunces, Georgia, serif;
  font-size: clamp(1.9rem, 3.5vw, 2.6rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  background: linear-gradient(120deg, #fff 15%, #a7f3d0 55%, #fbd38d 100%);
  background-size: 180% 180%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: sheen 12s ease-in-out infinite;
}
@keyframes sheen {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}
.lede {
  margin: 0.35rem 0 0;
  max-width: 32rem;
  color: var(--muted);
  font-size: 0.95rem;
  line-height: 1.4;
}

.entity-tabs {
  display: inline-flex;
  gap: 0.15rem;
  padding: 0.2rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.28);
}
.entity-tab {
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.58);
  padding: 0.48rem 1.05rem;
  border-radius: 7px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: color 0.2s, background 0.2s;
}
.entity-tab:hover { color: #fff; }
.entity-tab.active {
  background: linear-gradient(135deg, rgba(26, 188, 156, 0.32), rgba(251, 211, 141, 0.16));
  color: #ecfffa;
  box-shadow: inset 0 0 0 1px rgba(167, 243, 208, 0.22);
}

.scope-row {
  margin-bottom: 0.65rem;
}
.scope-tabs {
  display: inline-flex;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.25);
}
.scope-tab {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  padding: 0.4rem 0.95rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
}
.scope-tab.active {
  background: rgba(26, 188, 156, 0.22);
  color: #a7f3d0;
}

.loading-message {
  font-size: 1.05rem;
  font-style: italic;
  color: #ffe08a;
  margin: 1rem 0;
}
.count-msg {
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.82);
  margin: 0 0 0.55rem;
}

.scroll-box {
  height: 68vh;
  overflow: auto;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 4px;
  background: rgba(0, 0, 0, 0.15);
}
.starred-table {
  border-collapse: collapse;
  width: 100%;
  min-width: 640px;
}
.starred-table th {
  text-align: left;
  padding: 0.5rem;
  border-bottom: 2px solid #1abc9c;
  color: #fff;
  position: sticky;
  top: 0;
  background: rgba(12, 28, 32, 0.95);
}
.starred-table td {
  padding: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  vertical-align: top;
}
.name-link {
  color: #fbd38d;
  text-decoration: none;
  font-weight: 600;
}
.name-link:hover {
  color: #fff;
  text-decoration: underline;
}
.org-link {
  color: #a7f3d0;
  text-decoration: none;
}
.org-link:hover {
  color: #fff;
  text-decoration: underline;
}
.truncate {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}
.tag-pill {
  display: inline-block;
  margin: 0 0.25rem 0.25rem 0;
  padding: 0.12rem 0.45rem;
  border-radius: 6px;
  font-size: 0.75rem;
  background: rgba(26, 188, 156, 0.18);
  border: 1px solid rgba(26, 188, 156, 0.45);
  color: #a7f3d0;
}
.assigned-pill {
  display: inline-block;
  padding: 0.12rem 0.5rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(99, 102, 241, 0.2);
  border: 1px solid rgba(165, 180, 252, 0.45);
  color: #c7d2fe;
}
.starred-by {
  color: #c4b5fd;
  font-size: 0.85rem;
}
.muted { color: rgba(255, 255, 255, 0.45); }
.empty-row {
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  padding: 1.5rem;
}

.orgs-panel { margin-top: 0.25rem; }
.empty-orgs {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--muted);
  border: 1px dashed rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.18);
}
.empty-cta {
  display: inline-block;
  margin-top: 0.75rem;
  color: #a7f3d0;
  font-weight: 700;
  text-decoration: none;
  border-bottom: 1px solid rgba(167, 243, 208, 0.5);
}
.empty-cta:hover { color: #fff; }

.org-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
}
.org-card {
  padding: 1rem 1.05rem;
  border-radius: 12px;
  background: linear-gradient(160deg, rgba(26, 188, 156, 0.1), rgba(8, 24, 32, 0.55));
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: rise 0.45s ease both;
  animation-delay: calc(min(var(--i), 14) * 40ms);
  transition: border-color 0.2s, transform 0.2s, background 0.2s;
}
.org-card:hover {
  border-color: rgba(26, 188, 156, 0.4);
  transform: translateY(-2px);
  background: linear-gradient(160deg, rgba(26, 188, 156, 0.16), rgba(8, 24, 32, 0.65));
}
@keyframes rise {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.org-card-top {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
}
.star-btn {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  color: #fbd38d;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  margin-top: 0.1rem;
}
.star-btn:disabled { opacity: 0.5; cursor: wait; }
.org-name {
  font-family: Fraunces, Georgia, serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  text-decoration: none;
  line-height: 1.25;
  letter-spacing: -0.01em;
}
.org-name:hover { color: #a7f3d0; }
.org-meta {
  margin-top: 0.45rem;
  font-size: 0.82rem;
  color: var(--muted);
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.dot { opacity: 0.45; }
.org-tags { margin-top: 0.45rem; }
.website {
  display: inline-block;
  margin-top: 0.55rem;
  font-size: 0.8rem;
  color: #93c5fd;
  text-decoration: none;
  word-break: break-all;
}
.website:hover { text-decoration: underline; color: #bfdbfe; }

@media (max-width: 640px) {
  .hero h1 { font-size: 1.85rem; }
  .org-grid { grid-template-columns: 1fr; }
}
</style>
